import React, { useCallback, useEffect, useState } from "react";

// translations
import { SETTINGS } from "./constants";

// style
import styles from "./styles.module.scss";
import { InputType } from "../../components/forms/textInput";
import PrimaryButton from "../../components/common/PrimaryButton/PrimaryButton";
import CustomizedSnackbars from "../../components/customizedSnackbars";
import { getConfigs, setConfig } from "../../setup/api/services/config";
import TextInput from "../../components/forms/textInput/textInput";
import classNames from "classnames";
import CashoutBanks from "./components/CashoutBanks";

const Settings = () => {
  const [maximumPrice, setMaximumPrice] = useState<number>(0);
  const [maxPriceError, setMaxPriceError] = useState<string>("");
  const [serviceFee, setServiceFee] = useState<number>(0);
  const [serviceFeeError, setServiceFeeError] = useState<string>("");
  const [tabbyFee, setTabbyFee] = useState<number>(0);
  const [tabbyFeeError, setTabbyFeeError] = useState<string>("");

  const [deliveryPeriod, setDeliveryPeriod] = useState<number>(0);
  const [insepctionPeriod, setInspectionPeriod] = useState<number>(0);
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackbarTitle, setSnackbarTitle] = useState<string>("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">(
    "success"
  );

  const setSettingsConfig = useCallback(async () => {
    try {
      const config = await getConfigs();
      setMaximumPrice(Number(config?.data?.max_product_price?.value));
      setServiceFee(Number(config?.data?.commission?.value) * 100);
      setTabbyFee(Number(config?.data?.tabby_commission?.value) * 100);
      setDeliveryPeriod(Number(config?.data?.delivery_duration_default?.value));
      setInspectionPeriod(
        Number(config?.data?.inspection_duration_default?.value)
      );
    } catch (error) {
      console.error(JSON.stringify(error));
    }
  }, []);

  useEffect(() => {
    setSettingsConfig();
  }, [setSettingsConfig]);

  useEffect(() => {
    const interval = setTimeout(() => {
      setSnackBarOpen(false);
      setSnackbarTitle("");
    }, 5000);
    return () => {
      clearInterval(interval);
    };
  }, [snackBarOpen]);

  const setPriceLimit = useCallback(async () => {
    if (!maximumPrice || !serviceFee) return;
    try {
      const body = {
        max_product_price: maximumPrice,
        commission: serviceFee / 100,
        tabby_commission: tabbyFee / 100,
      };
      await setConfig(body);
      setSnackBarOpen(true);
      setSnackbarTitle(SETTINGS.SUCCESSFULY_CHANGED);
      setSnackbarType("success");
    } catch (error) {
      setSnackBarOpen(true);
      setSnackbarTitle(JSON.stringify(error));
      setSnackbarType("error");
    }
  }, [maximumPrice, serviceFee, tabbyFee]);

  const setDefaultPeriods = useCallback(async () => {
    if (!maximumPrice || !serviceFee) return;
    try {
      const body = {
        delivery_duration_default: deliveryPeriod,
        inspection_duration_default: insepctionPeriod,
      };
      await setConfig(body);
      setSnackBarOpen(true);
      setSnackbarTitle(SETTINGS.SUCCESSFULY_CHANGED);
      setSnackbarType("success");
    } catch (error) {
      setSnackBarOpen(true);
      setSnackbarTitle(JSON.stringify(error));
      setSnackbarType("error");
    }
  }, [deliveryPeriod, insepctionPeriod, maximumPrice, serviceFee]);

  useEffect(() => {
    if (maximumPrice <= 0) {
      setMaxPriceError(SETTINGS.ENTER_CORRECT_VALUE);
    } else {
      setMaxPriceError("");
    }
  }, [maximumPrice]);

  useEffect(() => {
    if (serviceFee > 100) {
      setServiceFeeError(SETTINGS.ENTER_CORRECT_VALUE);
    } else {
      setServiceFeeError("");
    }
  }, [serviceFee]);

  useEffect(() => {
    if (tabbyFee > 100) {
      setTabbyFeeError(SETTINGS.ENTER_CORRECT_VALUE);
    } else {
      setTabbyFeeError("");
    }
  }, [tabbyFee]);

  const renderPriceLimit = useCallback(() => {
    return (
      <div className={styles.container}>
        <div className={styles.price_section}>
          <h1 className={styles.mb16}>{SETTINGS.PRICE_LIMIT}</h1>
          <div className={classNames(styles["row"], styles["alignCenter"])}>
            <div className={classNames(styles["flex4"], styles.mb16)}>
              <TextInput
                label={SETTINGS.MAXIMUM_PRICE_OF_PRODUCT}
                defaultValue={maximumPrice}
                maxCharacters={50}
                required={true}
                type={InputType.NUMBER}
                handleChange={(e) => setMaximumPrice(Number(e.target.value))}
                description={SETTINGS.RIAL}
                errorMessage={maxPriceError}
                isSettings
              />
            </div>
            <div className={styles["flexDivider"]} />
            <div className={classNames(styles["flex4"], styles.mb16)}>
              <TextInput
                label={SETTINGS.SERVICE_FEE}
                defaultValue={serviceFee}
                maxCharacters={3}
                maxValue={100}
                required={true}
                type={InputType.NUMBER}
                handleChange={(e) => setServiceFee(Number(e.target.value))}
                description={"%"}
                errorMessage={serviceFeeError}
                isSettings
              />
            </div>
            <div className={styles["flexDivider"]} />
            <div className={classNames(styles["flex4"], styles.mb16)}>
              <TextInput
                label={SETTINGS.TABBY_FEE}
                defaultValue={tabbyFee}
                maxCharacters={3}
                maxValue={100}
                required={true}
                type={InputType.NUMBER}
                handleChange={(e) => setTabbyFee(Number(e.target.value))}
                description={"%"}
                errorMessage={tabbyFeeError}
                isSettings
              />
            </div>
            <div className={styles["flexDivider"]} />
            <div className={styles["flex2"]}>
              <PrimaryButton
                onClick={setPriceLimit}
                disabled={
                  !maximumPrice ||
                  !serviceFee ||
                  !tabbyFee ||
                  maxPriceError !== "" ||
                  serviceFeeError !== "" ||
                  tabbyFeeError !== ""
                }>
                {SETTINGS.CONFIRM}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    maxPriceError,
    maximumPrice,
    serviceFee,
    serviceFeeError,
    setPriceLimit,
    tabbyFee,
    tabbyFeeError,
  ]);

  const renderPeriods = useCallback(() => {
    return (
      <div className={styles.container}>
        <div className={styles.price_section}>
          <h1 className={styles.mb16}>{SETTINGS.HEADER}</h1>
          <div className={classNames(styles["row"], styles["alignCenter"])}>
            <div className={classNames(styles["flex4"], styles.mb16)}>
              <TextInput
                label={SETTINGS.DEFAULT_DELIVERY_PERIOD}
                defaultValue={deliveryPeriod}
                maxCharacters={3}
                maxValue={100}
                required={true}
                type={InputType.NUMBER}
                handleChange={(e) => setDeliveryPeriod(Number(e.target.value))}
                description={SETTINGS.DAY}
                isSettings
              />
            </div>
            <div className={styles["flexDivider"]} />
            <div className={classNames(styles["flex4"], styles.mb16)}>
              <TextInput
                label={SETTINGS.DEFAULT_INSPECTION_PERIOD}
                defaultValue={insepctionPeriod}
                maxCharacters={3}
                maxValue={100}
                required={true}
                type={InputType.NUMBER}
                handleChange={(e) =>
                  setInspectionPeriod(Number(e.target.value))
                }
                description={SETTINGS.DAY}
                isSettings
              />
            </div>
            <div className={styles["flexDivider"]} />
            <div className={styles["flex2"]}>
              <PrimaryButton
                onClick={setDefaultPeriods}
                disabled={
                  !deliveryPeriod ||
                  deliveryPeriod > 100 ||
                  !insepctionPeriod ||
                  insepctionPeriod > 100
                }>
                {SETTINGS.CONFIRM}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  }, [deliveryPeriod, insepctionPeriod, setDefaultPeriods]);

  return (
    <div className={styles["page_layout"]}>
      <h2 className={styles["header1"]}>{SETTINGS.DEFAULT_SETTINGS}</h2>
      {renderPriceLimit()}
      {renderPeriods()}
      <div className={styles.container}>
        <CashoutBanks />
      </div>
      {snackBarOpen && (
        <CustomizedSnackbars
          open={snackBarOpen}
          title={snackbarTitle}
          type={snackbarType}
        />
      )}
    </div>
  );
};

export default Settings;
